import {useEffect, useRef, useState} from 'react'
// Types
import type {Ballot, ParentCategory, InterstitialAd as InterstitialAdType} from '../../types/ballot'
// Utils
import {addAnalytic} from '../../lib/api'
import {keenClient} from '../../utils/analytics'
// Styles
import styles from './interstitial-ad.module.scss'

// Sample interstitial ad
// {
//   "_id": "5ec7126ee5ed3e09047bb915",
//   "name": "Interstitial Ad",
//   "advertise_name": "Take out. Stay home. Support Local ",
//   "value": "$0",
//   "website_link": "https://www.wweek.com/take-out-stay-home/",
//   "image_path": "https://ucarecdn.com/719cd409-374b-4370-89b0-da4c8241080b/",
//   "mobile_image_path": "https://ucarecdn.com/ddbe805c-e256-4786-81ef-16905ecb32d4/",
//   "updated_date_time": "2020-06-02T03:45:15.821Z"
// },

/** Runtime interstitial ads carry legacy API fields beyond the InterstitialAd type */
interface InterstitialAdData extends InterstitialAdType {
  name?: string
  advertise_name?: string
  website_link?: string
}

interface InterstitialAdProps {
  interstitialAd: InterstitialAdData
  ballot: Ballot | null
  group: ParentCategory | null
  hasSponsorshipAd?: boolean
  onPaginate: (direction: number) => void
}

const InterstitialAd = ({
  interstitialAd,
  ballot,
  group,
  hasSponsorshipAd = false,
  onPaginate,
}: InterstitialAdProps) => {
  const [isDesktop, setIsDesktop] = useState(false)
  const [adImageHeight, setAdImageHeight] = useState(600)
  const [adImageUrl, setAdImageUrl] = useState(interstitialAd?.mobile_image_path)
  const containerRef = useRef<HTMLDivElement>(null)
  const [adContainerHeightOffset, setAdContainerHeightOffset] = useState(66)

  const handleAdClick = () => {
    if (ballot?.new_features?.enable_best_of_internal_analytics) {
      addAnalytic({
        ballot_id: ballot?._id,
        category_id: group?._id,
        category_name: group?.parent_category_name,
        ad_clicks: {
          ad_id: interstitialAd._id,
          ad_name: interstitialAd.name,
          event_type: 'interstitial-Ads-Click',
        },
      })
    } else {
      keenClient.recordEvent('Interstitial-Ads-Click', {
        event_type: 'interstitial-Ads-Click',
        interstitialname: interstitialAd.name,
        ad_id: interstitialAd._id,
        category_id: group?._id,
        category_name: group?.parent_category_name,
        ballot_id: ballot?._id,
        interstitialAdName: interstitialAd.name,
        interstitialAdvertiseName: interstitialAd.advertise_name,
        ballot: ballot,
      })
    }
  }

  useEffect(() => {
    const handleResize = () => {
      const newIsDesktop = window.innerWidth >= 816
      setIsDesktop(newIsDesktop)

      let newAdImageUrl = interstitialAd?.mobile_image_path
      let newAdContainerHeightOffset = hasSponsorshipAd ? 90 : 66
      if (newIsDesktop) {
        newAdImageUrl = interstitialAd?.image_path
        newAdContainerHeightOffset = hasSponsorshipAd ? 114 : 90
      }
      setAdImageUrl(newAdImageUrl)
      setAdContainerHeightOffset(newAdContainerHeightOffset)

      const containerHeight = containerRef.current?.clientHeight ?? 0
      const buttonGroupHeight = newIsDesktop ? 112 : 88
      const verticalSpaceAlotted = containerHeight - buttonGroupHeight
      const imageIsTooWide = newIsDesktop && window.innerWidth <= 970

      const adImageRatio = newIsDesktop ? 250 / 970 : 300 / 600
      const maximumImageHeight = window.innerWidth * adImageRatio
      const containerIsTooShort = verticalSpaceAlotted < maximumImageHeight
      let newAdImageHeight = (imageIsTooWide && !containerIsTooShort) ? maximumImageHeight : verticalSpaceAlotted
      setAdImageHeight(newAdImageHeight)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div
      ref={containerRef}
      className={styles['__container']}
      style={
        isDesktop
          ? {
            height: `calc(100% - ${adContainerHeightOffset}px)`,
            transform:`translateY(calc(-50% - ${adContainerHeightOffset / 2}px))`,
          }
          : {height: `calc(100% - ${adContainerHeightOffset}px)`}
      }
    >
      <a
        href={interstitialAd?.website_link}
        target='_blank'
        className={styles['interstitial-ad']}
        rel="noreferrer"
      >
        {adImageUrl && (
          <img
            src={adImageUrl}
            alt={interstitialAd?.name || 'Interstitial ad'}
            onClick={() => handleAdClick()}
            style={{height: `${adImageHeight}px`}}
          />
        )}
      </a>
      <div className='buttons' style={{marginTop: '12px'}}>
        <button
          type='button'
          onClick={() => onPaginate(1)}
          className='button is-large is-dark'
        >
          Move to next
        </button>
      </div>
    </div>
  )
}

export default InterstitialAd
